/**
 * Caractéristiques COF2.
 *
 * 7 caracs : FOR, AGI, CON, INT, PER, CHA, VOL.
 * Les valeurs sont des **modificateurs directs** (bornes regles COF2 : -3 a +5 ; bornes systeme : CARAC_MIN/CARAC_MAX, plus larges pour couvrir les creatures).
 */

export const CARACS = ['FOR', 'AGI', 'CON', 'INT', 'PER', 'CHA', 'VOL'] as const;
export type Carac = (typeof CARACS)[number];

export type Caracs = Readonly<Record<Carac, number>>;

/**
 * Séries de distribution de caractéristiques utilisées à la création.
 * Le joueur distribue les 7 valeurs sur les 7 caracs comme il le souhaite.
 */
export const CARAC_SERIES = {
  polyvalent: [2, 2, 2, 1, 1, 0, -1],
  expert: [3, 2, 1, 1, 0, 0, -1],
  specialiste: [4, 2, 1, 0, 0, -1, -1],
} as const satisfies Record<string, readonly number[]>;

export type CaracSerieId = keyof typeof CARAC_SERIES;

/** Libellés humains des séries pour l'UI. */
export const CARAC_SERIES_META: Readonly<Record<CaracSerieId, { label: string; hint: string }>> = {
  polyvalent: {
    label: 'Polyvalent',
    hint: 'Équilibré, pas de point faible majeur.',
  },
  expert: {
    label: 'Expert',
    hint: 'Une force nette sans sacrifice trop lourd.',
  },
  specialiste: {
    label: 'Spécialiste',
    hint: 'Une carac dominante, des faiblesses assumées.',
  },
};

/**
 * Bornes systeme pour une carac (plus larges que les bornes regles COF2 -3/+5).
 * CARAC_MIN=-5 / CARAC_MAX=+10 couvrent les creatures exceptionnelles et
 * evitent les valeurs aberrantes lors de la saisie.
 */
export const CARAC_MIN = -5;
export const CARAC_MAX = 10;

/**
 * Rôles de chaque caractéristique (rappel des règles COF2).
 * Utilisé pour les tooltips et la documentation de la fiche.
 */
export const CARAC_ROLES: Readonly<Record<Carac, string>> = {
  FOR: "Attaque au contact, dommages au contact, port d'armure lourde.",
  AGI: 'Attaque à distance, esquive, défense de base.',
  CON: 'Points de vigueur, résistance, dés de récupération.',
  INT: 'Langues supplémentaires, lecture, magie du magicien.',
  PER: 'Initiative, magie du druide, perception, traque.',
  CHA: 'Points de chance, magie du barde/ensorceleur/prêtre.',
  VOL: 'Points de mana, attaque magique, résistance mentale.',
};

/**
 * Crée un objet Caracs initialisé à 0 pour toutes les caracs.
 */
export function emptyCaracs(): Record<Carac, number> {
  return { FOR: 0, AGI: 0, CON: 0, INT: 0, PER: 0, CHA: 0, VOL: 0 };
}

/**
 * Additionne deux snapshots de caracs (utile pour empiler base + peuple + capacités + buffs).
 */
export function addCaracs(a: Caracs, b: Caracs): Caracs {
  const out = emptyCaracs();
  for (const k of CARACS) out[k] = a[k] + b[k];
  return out;
}

/**
 * Garantit qu'un objet est bien une Caracs complète (toutes les clés présentes).
 * Retourne `true` si valide, `false` sinon.
 */
export function isCompleteCaracs(v: unknown): v is Caracs {
  if (v === null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return CARACS.every((k) => typeof o[k] === 'number' && Number.isFinite(o[k]));
}
